import { debounceTime, tap } from 'rxjs'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { ObjectStorage } from '@/infra/object-storage'
import { tapAction, toStream } from '@/utils/mobx-util'

export interface StoredValueOptions<T> {
  getStorage: () => ObjectStorage
  /**
   * Where the value is kept, or `null` while there is nowhere to keep it.
   */
  getKey: () => string | null
  getValue: () => T
  /**
   * Takes what is stored under the key, `null` when nothing is. Runs in an
   * action whenever the key changes.
   */
  load: (stored: unknown) => void
}

/**
 * A value kept in storage under a key that can change: it is loaded when the
 * key changes, and saved shortly after it changes and when deactivated.
 */
export class StoredValue<T> implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  constructor(private readonly _options: StoredValueOptions<T>) {
    this._lifecycle.manage(
      toStream(_options.getKey, true).pipe(
        tapAction((key: string | null) =>
          _options.load(key ? _options.getStorage().getItem(key) : null),
        ),
      ),
    )
    this._lifecycle.manage(
      toStream(_options.getValue).pipe(
        debounceTime(500),
        tap(() => this._save()),
      ),
    )
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    // Saves a change made right before leaving instead of dropping it.
    this._save()
    this._lifecycle.deactivate()
  }

  private _save() {
    const key = this._options.getKey()
    if (key) {
      this._options.getStorage().setItem(key, this._options.getValue())
    }
  }
}
